import { Router } from 'express';
import { db } from '../storage';
import { TuitionInvoice } from '../../src/types';

export const invoicesRouter = Router();

// GET /api/invoices - List invoices with filters
invoicesRouter.get('/', (req, res) => {
  const teacherId = req.query.teacherId as string;
  const month = req.query.month as string | undefined;
  const status = req.query.status as string | undefined;
  const classId = req.query.classId as string | undefined;
  const search = req.query.search as string | undefined;

  if (!teacherId) {
    return res.status(400).json({ error: 'teacherId parameter is required' });
  }

  // If no invoices exist for this teacher at all, auto generate for current month
  let list = db.getInvoices(teacherId, month, status, classId, search);
  if (list.length === 0 && teacherId !== 'admin_master' && teacherId !== 'all' && (!status || status === 'all') && (!classId || classId === 'all') && !search) {
    const currentMonth = month || new Date().toISOString().substring(0, 7);
    db.generateMonthlyInvoices(teacherId, currentMonth);
    list = db.getInvoices(teacherId, month, status, classId, search);
  }

  res.json({ invoices: list });
});

// GET /api/invoices/stats - Summary statistics
invoicesRouter.get('/stats', (req, res) => {
  const teacherId = req.query.teacherId as string;
  const month = req.query.month as string | undefined;

  if (!teacherId) {
    return res.status(400).json({ error: 'teacherId parameter is required' });
  }

  const stats = db.getInvoiceStats(teacherId, month);
  res.json({ stats });
});

// GET /api/invoices/:id - Single invoice
invoicesRouter.get('/:id', (req, res) => {
  const teacherId = req.query.teacherId as string;
  const { id } = req.params;

  if (!teacherId) {
    return res.status(400).json({ error: 'teacherId is required' });
  }

  const invoice = db.getInvoiceById(teacherId, id);
  if (!invoice) {
    return res.status(404).json({ error: 'Invoice not found' });
  }

  res.json({ invoice });
});

// POST /api/invoices - Create new invoice
invoicesRouter.post('/', (req, res) => {
  const {
    teacherId,
    studentId,
    studentName,
    parentName,
    parentPhone,
    classId,
    className,
    month,
    periodDescription,
    sessionCount,
    unitPrice,
    totalAmount,
    discountAmount,
    finalAmount,
    paidAmount,
    status,
    paymentMethod,
    notes,
    dueDate
  } = req.body;

  if (!teacherId || !studentId || !classId) {
    return res.status(400).json({ error: 'teacherId, studentId, and classId are required' });
  }

  const teacher = db.getUserById(teacherId);
  const now = new Date();
  const yearMonth = month || now.toISOString().substring(0, 7);
  const codeSuffix = Math.floor(100 + Math.random() * 900);
  const invoiceCode = req.body.invoiceCode || `HD-${yearMonth.replace('-', '')}-${studentId.substring(0, 4).toUpperCase()}-${codeSuffix}`;

  const calcFinal = finalAmount !== undefined ? finalAmount : (totalAmount - (discountAmount || 0));
  const calcPaid = paidAmount || 0;

  let qrUrl = '';
  if (teacher?.bankName && teacher?.bankAccountNumber && teacher?.bankAccountName) {
    let bankCode = 'MB';
    const bn = teacher.bankName.toUpperCase();
    if (bn.includes('VIETCOM') || bn.includes('VCB')) bankCode = 'VCB';
    else if (bn.includes('TECHCOM') || bn.includes('TCB')) bankCode = 'TCB';
    else if (bn.includes('MB')) bankCode = 'MB';
    else if (bn.includes('VPB') || bn.includes('VPBANK')) bankCode = 'VPB';
    else if (bn.includes('ACB')) bankCode = 'ACB';
    else if (bn.includes('BIDV')) bankCode = 'BIDV';
    else if (bn.includes('AGRI') || bn.includes('VBA')) bankCode = 'VBA';
    else if (bn.includes('TPB') || bn.includes('TPBANK')) bankCode = 'TPB';

    const addInfo = `HP ${studentName || 'HocVien'} ${className || ''} ${yearMonth}`;
    qrUrl = `https://img.vietqr.io/image/${bankCode}-${teacher.bankAccountNumber}-compact2.png?amount=${Math.max(0, calcFinal - calcPaid)}&addInfo=${encodeURIComponent(addInfo)}&accountName=${encodeURIComponent(teacher.bankAccountName)}`;
  }

  const newInvoice: TuitionInvoice = {
    id: `inv_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
    invoiceCode,
    teacherId,
    studentId,
    studentName: studentName || 'Học viên',
    parentName: parentName || '',
    parentPhone: parentPhone || '',
    classId,
    className: className || 'Lớp học',
    month: yearMonth,
    periodDescription: periodDescription || `Học phí ${yearMonth}`,
    sessionCount: sessionCount || 1,
    unitPrice: unitPrice || calcFinal,
    totalAmount: totalAmount || calcFinal,
    discountAmount: discountAmount || 0,
    finalAmount: calcFinal,
    paidAmount: calcPaid,
    status: status || (calcPaid >= calcFinal ? 'paid' : calcPaid > 0 ? 'partial' : 'pending'),
    paymentMethod: paymentMethod,
    paidAt: calcPaid > 0 ? now.toISOString() : undefined,
    notes: notes || '',
    dueDate: dueDate || `${yearMonth}-10`,
    vietQrUrl: qrUrl,
    createdAt: now.toISOString()
  };

  const created = db.createInvoice(newInvoice);
  res.status(201).json({ invoice: created });
});

// POST /api/invoices/generate-monthly - Batch generate for month
invoicesRouter.post('/generate-monthly', (req, res) => {
  const { teacherId, month } = req.body;
  if (!teacherId || !month) {
    return res.status(400).json({ error: 'teacherId and month are required' });
  }

  const generated = db.generateMonthlyInvoices(teacherId, month);
  res.json({
    message: `Đã khởi tạo thành công ${generated.length} hóa đơn cho tháng ${month}`,
    count: generated.length,
    invoices: generated
  });
});

// PUT /api/invoices/:id - Update invoice
invoicesRouter.put('/:id', (req, res) => {
  const { id } = req.params;
  const { teacherId, ...updates } = req.body;

  if (!teacherId) {
    return res.status(400).json({ error: 'teacherId is required' });
  }

  // Recalculate status if paidAmount or finalAmount changed
  if (updates.paidAmount !== undefined || updates.finalAmount !== undefined) {
    const existing = db.getInvoiceById(teacherId, id);
    if (existing) {
      const finalAmt = updates.finalAmount !== undefined ? updates.finalAmount : existing.finalAmount;
      const paidAmt = updates.paidAmount !== undefined ? updates.paidAmount : existing.paidAmount;
      if (paidAmt >= finalAmt && finalAmt > 0) {
        updates.status = 'paid';
        if (!updates.paidAt && !existing.paidAt) updates.paidAt = new Date().toISOString();
      } else if (paidAmt > 0) {
        updates.status = 'partial';
      } else {
        updates.status = 'pending';
      }
    }
  }

  const updated = db.updateInvoice(teacherId, id, updates);
  if (!updated) {
    return res.status(404).json({ error: 'Invoice not found' });
  }

  res.json({ invoice: updated });
});

// DELETE /api/invoices/:id - Delete invoice
invoicesRouter.delete('/:id', (req, res) => {
  const teacherId = req.query.teacherId as string;
  const { id } = req.params;

  if (!teacherId) {
    return res.status(400).json({ error: 'teacherId is required' });
  }

  const success = db.deleteInvoice(teacherId, id);
  if (!success) {
    return res.status(404).json({ error: 'Invoice not found or already deleted' });
  }

  res.json({ success: true, message: 'Đã xóa hóa đơn thành công' });
});

// POST /api/invoices/:id/send-zalo - Send invoice notification
invoicesRouter.post('/:id/send-zalo', (req, res) => {
  const teacherId = req.body.teacherId;
  const { id } = req.params;

  if (!teacherId) {
    return res.status(400).json({ error: 'teacherId is required' });
  }

  const invoice = db.getInvoiceById(teacherId, id);
  const teacher = db.getUserById(teacherId);

  if (!invoice) {
    return res.status(404).json({ error: 'Invoice not found' });
  }

  const content = `[${teacher?.centerName || teacher?.name || 'Giáo viên'}] Kính gửi Phụ huynh em ${invoice.studentName}, thông báo phiếu thu học phí ${invoice.periodDescription}: Số tiền cần thanh toán: ${invoice.finalAmount.toLocaleString('vi-VN')} VNĐ (Đã đóng: ${invoice.paidAmount.toLocaleString('vi-VN')} VNĐ). STK chuyển khoản: ${teacher?.bankName || ''} - ${teacher?.bankAccountNumber || ''} (${teacher?.bankAccountName || ''}). Cảm ơn Quý Phụ huynh!`;

  const log = db.addZaloLog({
    id: `log_${Date.now()}`,
    teacherId,
    type: 'tuition_notice',
    recipientName: `${invoice.parentName || 'Phụ huynh'} (PH em ${invoice.studentName})`,
    recipientPhone: invoice.parentPhone || '0900000000',
    content,
    status: 'sent',
    sentAt: new Date().toISOString()
  });

  res.json({
    success: true,
    message: `Đã gửi thông báo hóa đơn thành công qua Zalo tới ${invoice.parentPhone || 'Phụ huynh'}`,
    log
  });
});
